'use client';

import React, { useState, useEffect } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToForm = () => {
    const formEl = document.getElementById('qualify-form-section');
    if (formEl) {
      formEl?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Not on home page — navigate to home with anchor
      window.location.href = '/home#qualify-form-section';
    }
  };

  const navLinks = [
    { label: 'About Us', href: '/about' },
    { label: 'How It Works', href: '/how-it-works' },
    { label: 'Loan Guide', href: '/loan-guide' },
    { label: 'FAQ', href: '/faq' },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-md shadow-card border-b border-slate-mist/40'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5">
          <AppLogo
            text="Lenders Home"
            iconName="BanknotesIcon"
            size={32}
            className="text-slate"
          />
        </Link>

        {/* Nav links - desktop */}
        <nav className="hidden lg:flex items-center gap-6">
          {navLinks?.map((item) => (
            <Link
              key={item?.label}
              href={item?.href}
              className="text-sm font-medium text-slate-fog hover:text-slate transition-colors duration-200 font-body"
            >
              {item?.label}
            </Link>
          ))}
        </nav>

        {/* CTA */}
        <div className="flex items-center gap-3">
          <Link
            href="/login"
            className="text-sm font-medium text-slate-fog hover:text-slate transition-colors duration-200 font-body hidden sm:block"
          >
            Sign In
          </Link>
          <button
            onClick={scrollToForm}
            className="flex items-center gap-2 px-5 py-2.5 bg-sky text-white rounded-full text-sm font-semibold font-body hover:bg-sky-dark transition-all duration-200 shadow-sky/30 shadow-md"
          >
            <span className="hidden sm:inline">Find My Loan Options</span>
            <span className="sm:hidden">Get Started</span>
            <Icon name="ArrowRightIcon" size={15} />
          </button>
          {/* Mobile menu toggle */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden p-2 rounded-lg text-slate-fog hover:text-slate hover:bg-cloud transition-all"
            aria-label="Toggle menu"
          >
            <Icon name={menuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={20} />
          </button>
        </div>
      </div>
      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-md border-t border-slate-mist/30 px-6 py-4 space-y-1">
          {navLinks?.map((item) => (
            <Link
              key={item?.label}
              href={item?.href}
              onClick={() => setMenuOpen(false)}
              className="block px-3 py-2.5 rounded-xl text-sm font-medium text-slate-fog hover:text-slate hover:bg-cloud transition-all font-body"
            >
              {item?.label}
            </Link>
          ))}
          <div className="pt-2 border-t border-slate-mist/30 mt-2">
            <Link href="/login" onClick={() => setMenuOpen(false)} className="block px-3 py-2.5 rounded-xl text-sm font-medium text-slate-fog hover:text-slate hover:bg-cloud transition-all font-body">
              Sign In
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}